/**
 * Submission media helpers shared by the judges' page (Evaluations) and Present mode.
 * Pure functions only; rendering lives in the sibling viewer components.
 */
import { getMultiSubmissionConfig } from '../common/multi-submission/configs';

export interface MultiArtifact {
  original_name?: string;
  stored_name?: string;
  content_type?: string;
  size_bytes?: number;
  object_key?: string;
  url?: string;
}

export interface MultiFieldOption {
  value: string;
  label: string;
}

export interface MultiField {
  id?: string;
  label?: string;
  type?: string;
  value?: unknown;
  options?: MultiFieldOption[];
}

export interface MultiManifest {
  schema?: string;
  submitted_at?: string;
  form?: {
    schema?: string;
    fields?: MultiField[];
    values?: Record<string, unknown>;
  };
  fields?: MultiField[];
  values?: Record<string, unknown>;
  artifacts?: MultiArtifact[];
}

/** What the viewers read from a multi submission; judge and Present payloads both satisfy it. */
export interface ViewableMultiSubmission {
  submission_id: number;
  submission_version: number;
  url: string;
  manifest?: MultiManifest | null;
  team_name?: string | null;
  metadata_track?: string | null;
  metadata_team?: string | null;
  eval_count?: number;
  already_scored?: boolean;
}

export function formatBytes(size?: number): string {
  if (!size || Number.isNaN(size)) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function artifactName(artifact: MultiArtifact): string {
  return artifact.original_name || artifact.stored_name || artifact.object_key || '';
}

export function findArtifactByExtensions(artifacts: MultiArtifact[] | undefined, extensions: string[]): MultiArtifact | null {
  if (!Array.isArray(artifacts)) return null;
  const normalizedExtensions = extensions.map((ext) => ext.toLowerCase());
  return (
    artifacts.find((artifact) => {
      const name = artifactName(artifact).toLowerCase();
      return normalizedExtensions.some((ext) => name.endsWith(ext));
    }) || null
  );
}

export function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function normalizeHttpUrl(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (isValidHttpUrl(trimmed)) return trimmed;
  const looksLikeHostPath = /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}(\/|$)/.test(trimmed);
  if (looksLikeHostPath) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

export function toEmbeddableVideoUrl(value: string): string {
  if (!isValidHttpUrl(value)) return value;
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.replace('/', '').trim();
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    if (host.endsWith('youtube.com')) {
      if (parsed.pathname === '/watch') {
        const id = parsed.searchParams.get('v');
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        const id = parsed.pathname.split('/')[2];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
    }

    if (host === 'drive.google.com') {
      const fileMatch = parsed.pathname.match(/\/file\/d\/([^/]+)/);
      if (fileMatch?.[1]) {
        return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;
      }
      const queryId = parsed.searchParams.get('id');
      if (queryId) {
        return `https://drive.google.com/file/d/${queryId}/preview`;
      }
    }

    if (host.endsWith('loom.com') && parsed.pathname.startsWith('/share/')) {
      const id = parsed.pathname.split('/')[2];
      if (id) return `https://www.loom.com/embed/${id}`;
    }
  } catch {
    return value;
  }

  return value;
}

export function getManifestFieldValue(manifest: MultiManifest | null | undefined, fieldId: string): unknown {
  if (!manifest) return undefined;
  const fromValues = manifest.form?.values?.[fieldId] ?? manifest.values?.[fieldId];
  if (typeof fromValues !== 'undefined') {
    return fromValues;
  }
  const field = (manifest.form?.fields || manifest.fields || []).find((entry) => entry.id === fieldId);
  return field?.value;
}

export function getStringManifestFieldValue(manifest: MultiManifest | null | undefined, fieldId: string): string {
  const raw = getManifestFieldValue(manifest, fieldId);
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number') return String(raw);
  return '';
}

// Competition-specific project layout: keep every 14/18 decision here.
const PROJECT_LAYOUT_COMPETITIONS = [14, 18];

export function usesProjectLayout(competitionId: number): boolean {
  return PROJECT_LAYOUT_COMPETITIONS.includes(competitionId);
}

export interface ProjectLabels {
  slidesHeading: string;
  descriptionHeading: string;
  emptyDescription: string;
  showAgentLink: boolean;
  showPptDownload: boolean;
}

export function projectLabels(competitionId: number): ProjectLabels {
  if (competitionId === 18) {
    return {
      slidesHeading: 'Project slides (PDF)',
      descriptionHeading: 'Project description',
      emptyDescription: 'No project description available.',
      showAgentLink: false,
      showPptDownload: true,
    };
  }
  return {
    slidesHeading: 'Presentation (PDF)',
    descriptionHeading: 'Brief Explanation',
    emptyDescription: 'No brief explanation available.',
    showAgentLink: true,
    showPptDownload: false,
  };
}

export function trackLabelFor(competitionId: number, rawTrack?: string | null): string {
  const normalized = (rawTrack || '').trim();
  const trackField = getMultiSubmissionConfig(competitionId).fields.find((field) => field.id === 'track');
  return trackField?.options?.find((option) => option.value === normalized)?.label || normalized || '-';
}
